import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import type { CapabilityDefinition } from "@energyplatform/client";
import { api } from "../services.ts";
import { t, tOr } from "../i18n.ts";
import { capabilityId } from "../model.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { PageHeader, Skeleton, Tabs } from "../components/ui.tsx";

const TABS = ["capabilities", "templates"] as const;
type Tab = (typeof TABS)[number];

/**
 * Capability catalog (prototype screen "Katalog możliwości"): what devices can measure and do, independent of the
 * vendor, and the device templates that map vendor data onto it.
 */
export function CatalogPage() {
  const [capabilities] = createResource(() => api.catalog.capabilities());
  const [templates] = createResource(() => api.catalog.deviceTemplates());
  const [tab, setTab] = createSignal<Tab>("capabilities");
  const [selected, setSelected] = createSignal<string>();
  const [query, setQuery] = createSignal("");
  const current = () =>
    capabilities()?.find((c) => c.ref === selected()) ?? capabilities()?.[0];
  const usedBy = (ref: string) =>
    (templates() ?? []).filter((tpl) =>
      tpl.channels.some((c) => c.capability === ref),
    );

  const filteredTemplates = createMemo(() => {
    const q = query().trim().toLocaleLowerCase();
    return (templates() ?? []).filter(
      (tpl) =>
        q === "" ||
        `${tpl.vendor} ${tpl.model} ${tpl.id} ${tpl.adapter}`
          .toLocaleLowerCase()
          .includes(q),
    );
  });

  return (
    <>
      <PageHeader
        eyebrow={t("nav.configuration")}
        title={t("catalog.title")}
        sub={t("catalog.intro")}
      />
      <section class="card">
        <Tabs
          tabs={TABS}
          value={tab()}
          onChange={setTab}
          label={(x) =>
            x === "capabilities"
              ? t("catalog.capabilities", {
                  count: capabilities()?.length ?? 0,
                })
              : t("catalog.templates", { count: templates()?.length ?? 0 })
          }
        />
        <Show when={tab() === "capabilities"}>
          <Show
            when={!capabilities.error}
            fallback={
              <div class="card-b">
                <ErrorBox error={capabilities.error} />
              </div>
            }
          >
            <Show when={capabilities()} fallback={<Skeleton />}>
              <div class="res list" style={{ padding: "14px" }}>
                <div class="cards" style={{ "grid-template-columns": "1fr" }}>
                  <For each={capabilities()}>
                    {(c) => (
                      <button
                        type="button"
                        class="rc"
                        aria-pressed={c.ref === current()?.ref}
                        onClick={() => setSelected(c.ref)}
                      >
                        <h3>
                          {tOr(`capability.${capabilityId(c.ref)}`, c.ref)}
                        </h3>
                        <p class="mono">{c.ref}</p>
                        <p>
                          {t("catalog.usedBy", { count: usedBy(c.ref).length })}
                        </p>
                      </button>
                    )}
                  </For>
                </div>
                <Show when={current()}>
                  {(c) => (
                    <CapabilityDetail
                      capability={c()}
                      templates={usedBy(c().ref).map(
                        (tpl) => `${tpl.vendor} · ${tpl.model}`,
                      )}
                    />
                  )}
                </Show>
              </div>
            </Show>
          </Show>
        </Show>
        <Show when={tab() === "templates"}>
          <div class="panel-h">
            <label class="search">
              <Icon name="search" />
              <input
                type="search"
                placeholder={t("catalog.search")}
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
              />
            </label>
          </div>
          <Show
            when={!templates.error}
            fallback={
              <div class="card-b">
                <ErrorBox error={templates.error} />
              </div>
            }
          >
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>{t("catalog.model")}</th>
                    <th>{t("catalog.adapter")}</th>
                    <th>{t("catalog.channels")}</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={filteredTemplates()}>
                    {(tpl) => (
                      <tr>
                        <td>
                          {tpl.vendor} · {tpl.model}
                          <div class="hint mono">{tpl.id}</div>
                        </td>
                        <td>{tOr(`adapter.${tpl.adapter}`, tpl.adapter)}</td>
                        <td>
                          <div class="q">
                            <For each={tpl.channels}>
                              {(ch) => (
                                <span class="chip">
                                  {tOr(`channel.${ch.key}`, ch.key)} ·{" "}
                                  {tOr(
                                    `capability.${capabilityId(ch.capability)}`,
                                    ch.capability,
                                  )}
                                </span>
                              )}
                            </For>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </Show>
      </section>
    </>
  );
}

function CapabilityDetail(props: {
  capability: CapabilityDefinition;
  templates: string[];
}) {
  return (
    <section class="card">
      <div class="card-h">
        <h2>
          {tOr(
            `capability.${capabilityId(props.capability.ref)}`,
            props.capability.ref,
          )}
        </h2>
        <span class="meta mono">{props.capability.provider}</span>
      </div>
      <table class="tbl">
        <thead>
          <tr>
            <th>{t("catalog.attribute")}</th>
            <th>{t("catalog.kind")}</th>
            <th>{t("catalog.unit")}</th>
          </tr>
        </thead>
        <tbody>
          <For each={props.capability.attributes}>
            {(a) => (
              <tr>
                <td>
                  {tOr(`attribute.${a.key}`, a.key)}
                  <div class="hint mono">{a.key}</div>
                </td>
                <td>{t(`kind.${a.kind}`)}</td>
                <td>
                  {a.enumValues
                    ? a.enumValues.map((v) => tOr(`enum.${v}`, v)).join(" / ")
                    : tOr(`unit.${a.unit}`, a.unit)}
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
      <div class="card-b stack">
        <Show when={props.capability.commands.length > 0}>
          <div>
            <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
              {t("catalog.commands")}
            </div>
            <div class="q">
              <For each={props.capability.commands}>
                {(cmd) => (
                  <span class="chip">
                    {tOr(`command.${cmd.name}`, cmd.name)}
                  </span>
                )}
              </For>
            </div>
          </div>
        </Show>
        <div>
          <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
            {t("catalog.usedByTitle")}
          </div>
          <Show
            when={props.templates.length > 0}
            fallback={<span class="hint">{t("catalog.unused")}</span>}
          >
            <div class="q">
              <For each={props.templates}>
                {(name) => <span class="chip">{name}</span>}
              </For>
            </div>
          </Show>
        </div>
      </div>
    </section>
  );
}
